import {
  Button,
  Card,
  CardContent,
  Fade,
  Grid,
  IconButton,
  Modal,
} from '@material-ui/core'
import { AddCircle, Close, Edit } from '@material-ui/icons'
import { Typography } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { getPartsByUserId } from '../../../actions/partActions'
import defaultPartThumb from '../../../assets/img/default-part.png'
import { PART_UPDATE_RESET } from '../../../constants/partConstants'
import {
  PROJECT_CURRENT_STEP,
  PROJECT_EXIT_REQUEST,
} from '../../../constants/projectConstants'
import { useDateFormatter } from '../../../hooks/useDateFormatter'
import { Context } from '../../../providers/ProjectContextProvider'
import FormContainer from '../../Form/FormContainer'
import Loader from '../../Loader/Loader'
import Message from '../../Message/Message'
import ToastMessage from '../../ToastMessage/ToastMessage'
import PartRequestTable from './PartRequestTable'

interface SelectPartProps {
  setShowAddPart: (val: boolean) => void
  selectedPartId: string
  setSelectedPartId: (partId: string) => void
  setPartToEdit: (val: Part | undefined) => void
  setPartTitleOverride: (title: string) => void
}

interface PartProps {
  part: Part
}

const SelectPart = ({
  setShowAddPart,
  selectedPartId,
  setSelectedPartId,
  setPartToEdit,
  setPartTitleOverride,
}: SelectPartProps) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const { setShowNotifyUser } = useContext(Context)

  const [nextIsReady, setNextIsReady] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [updatePartToast, setUpdatePartToast] = useState(false)
  const [updatedPartName, setUpdatedPartName] = useState('')
  const [partIsComplete, setPartIsComplete] = useState(true)
  const [partsSet, setPartsSet] = useState(false)

  const { userInfo } = useSelector(
    (state: { userLogin: UserLogin }) => state.userLogin
  )
  const { project } = useSelector(
    (state: RootStateOrAny) => state.projectDetails
  )
  const { error, loading, parts } = useSelector(
    (state: RootStateOrAny) => state.partList
  )
  const { part: updatedPart, success: partUpdateSuccess } = useSelector(
    (state: RootStateOrAny) => state.partUpdate
  )

  useEffect(() => {
    if (!userInfo) {
      history.push(`/login?redirect=${window.location.pathname}`)
    }

    if (partUpdateSuccess) {
      setPartToEdit(undefined)
      setPartIsComplete(
        updatedPart.partProgramFiles.length > 0 ||
          updatedPart.partProgramLink !== ''
          ? true
          : false
      )
      setUpdatedPartName(updatedPart.name)
      setUpdatePartToast(true)
      dispatch({ type: PART_UPDATE_RESET })
      window.scrollTo(0, 0)
    }

    if (selectedPartId !== '') {
      setNextIsReady(true)
    } else {
      setNextIsReady(false)
    }

    if (project && project.requests) {
      if (project.requests.length > 0) {
        setNextIsReady(true)
      }
    }
  }, [
    dispatch,
    userInfo,
    setSelectedPartId,
    setNextIsReady,
    selectedPartId,
    project,
    partUpdateSuccess,
    setPartToEdit,
    updatedPart,
    history,
  ])

  useEffect(() => {
    if (!partsSet && Object.keys(project).length !== 0) {
      dispatch(getPartsByUserId(project.user))
      setPartsSet(true)
    }
  }, [dispatch, userInfo, setPartsSet, project, partsSet])

  const toggleSelectedPart = (part: Part) => {
    setPartIsComplete(
      part.partProgramFiles.length > 0 || part.partProgramLink !== ''
        ? true
        : false
    )
    setSelectedPartId(part._id === selectedPartId ? '' : part._id)
    setPartToEdit(part)
  }

  const handleNext = () => {
    if (
      selectedPartId !== '' &&
      !partIsComplete &&
      project.type === 'programming'
    ) {
      setShowAddPart(true)
      setPartTitleOverride('Please complete part details to continue')
      return
    } else {
      setPartTitleOverride('')
    }

    if (selectedPartId !== '') {
      dispatch({
        type: PROJECT_CURRENT_STEP,
        payload: 3,
      })
    } else {
      setShowModal(true)
    }
  }

  const handleAddNewPartClick = () => {
    setPartToEdit(undefined)
    setShowAddPart(true)
  }

  const handleSaveAndExit = () => {
    if (userInfo.isAdmin) {
      setShowNotifyUser(true)
    } else {
      dispatch({
        type: PROJECT_EXIT_REQUEST,
      })
    }
  }

  const EmptyPartState = () => {
    return (
      <Typography
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        There are currently no parts in your library. Add a new part to proceed.
      </Typography>
    )
  }

  const ProceedModal = () => {
    return (
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        aria-labelledby="Finish adding parts modal"
        aria-describedby="Finish adding parts modal"
      >
        <Fade in={showModal}>
          <div className="modal-window">
            <div className="modal-header">
              <div>Finish adding parts</div>
              <IconButton onClick={() => setShowModal(false)}>
                <Close />
              </IconButton>
            </div>
            <div className="modal-body">
              <h4 className="m-b-32">
                Are you finished adding parts to your quote request?
              </h4>
              <p>
                You can select more parts to add to this request or proceed to
                the next step to view your quote estimate.
              </p>
              <div className="actions m-t-40 flex-end flex">
                <Button
                  color="primary"
                  variant="outlined"
                  className="m-r-24"
                  onClick={() => setShowModal(false)}
                >
                  No, I want to add parts
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() =>
                    dispatch({
                      type: PROJECT_CURRENT_STEP,
                      payload: 5,
                    })
                  }
                >
                  Yes, proceed to quote estimate
                </Button>
              </div>
            </div>
          </div>
        </Fade>
      </Modal>
    )
  }

  const Part = ({ part }: PartProps) => {
    const handleEditClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      setPartTitleOverride('')
      setPartToEdit(part)
      setShowAddPart(true)
    }

    return (
      <div
        className={`card-wrapper ${selectedPartId === part._id && 'selected'}`}
        onClick={() => toggleSelectedPart(part)}
      >
        <Card className={`part white-card`}>
          <div
            className={`thumbnail-container ${
              !part.thumbnail.id && 'no-thumbnail part'
            }`}
            style={{
              backgroundImage: part.thumbnail.id
                ? `url(${process.env.REACT_APP_API}/api/thumbnail/${part.thumbnail.id})`
                : `url(${defaultPartThumb})`,
            }}
          ></div>
          <IconButton
            className="hover-edit"
            onClick={(e) => handleEditClick(e)}
          >
            <Edit />
          </IconButton>

          <CardContent>
            <div className="content">
              <h4>{part.name}</h4>
              <p style={{ minHeight: 20 }}>
                {part.size.x && (
                  <>
                    {part.size.x}/{part.size.y}/{part.size.z} {part.size.units}
                  </>
                )}
              </p>
              <p>{part.tolerance}</p>

              <div className="flex space-between last-updated">
                <p>
                  <strong className="primary">Last updated:</strong>
                </p>
                <p>{useDateFormatter(part.updatedAt)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="select-part">
      <FormContainer>
        <div className="flex space-between">
          <h4 className="m-b-30">
            Select part {userInfo.isAdmin && `for ${project.userName}`}
          </h4>
          <div>
            <Button
              className="m-l-16"
              color="primary"
              onClick={handleAddNewPartClick}
              startIcon={<AddCircle />}
              variant="contained"
            >
              Add new part
            </Button>
          </div>
        </div>

        <Grid
          container
          spacing={1}
          className={`scroll-container  ${
            parts && parts.length > 6 ? 'scroll' : ''
          }`}
        >
          {loading ? (
            <Loader />
          ) : error ? (
            <Message variant="danger">{error}</Message>
          ) : parts && parts.length > 0 ? (
            parts.map((part: Part) => (
              <Grid item md={4} sm={6} key={part._id}>
                <Part part={part} />
              </Grid>
            ))
          ) : (
            <EmptyPartState />
          )}
        </Grid>

        {project && project.requests && (
          <PartRequestTable requests={project.requests} />
        )}

        <div className="actions m-t-40">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveAndExit}
          >
            Save and exit
          </Button>

          <div>
            <Button
              variant="outlined"
              color="primary"
              onClick={() =>
                dispatch({
                  type: PROJECT_CURRENT_STEP,
                  payload: 1,
                })
              }
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              style={{ marginLeft: 20 }}
              onClick={handleNext}
              disabled={!nextIsReady}
            >
              Next
            </Button>
          </div>
        </div>
      </FormContainer>

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Part updated</p>
            <p className="fs14 m-b-0">
              You successfully updated {updatedPartName}.
            </p>
          </>
        }
        open={updatePartToast}
        setOpen={setUpdatePartToast}
      />

      {showModal && <ProceedModal />}
    </div>
  )
}

export default SelectPart
